/**
 * Public page that consumes a one-time token from the account deletion email
 * and cancels the pending soft-delete (restoring the user to active).
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, AlertTriangle } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { userApi } from '@/shared/users/services/userApi';
import { getErrorCode } from '../../shared/utils/errors';
import { PAGE_TITLE_COMPACT } from '../../shared/styles/typography';

type ViewState =
  | { kind: 'verifying' }
  | { kind: 'cancelled' | 'already_cancelled' }
  | { kind: 'invalid' | 'expired' };

export function CancelAccountDeletionPage() {
  const { t } = useTranslation(['user_management', 'common']);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [view, setView] = useState<ViewState>({ kind: 'verifying' });

  useEffect(() => {
    if (!token) {
      setView({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    userApi
      .cancelAccountDeletion(token)
      .then((res) => {
        if (cancelled) return;
        setView({ kind: res.data.status });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code = getErrorCode(err);
        setView({ kind: code === 'TOKEN_EXPIRED' ? 'expired' : 'invalid' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const renderBody = () => {
    if (view.kind === 'verifying') {
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{t('user_management:settings.cancelDeletion.verifying')}</span>
        </div>
      );
    }
    if (view.kind === 'cancelled' || view.kind === 'already_cancelled') {
      const titleKey =
        view.kind === 'cancelled'
          ? 'user_management:settings.cancelDeletion.successTitle'
          : 'user_management:settings.cancelDeletion.alreadyCancelledTitle';
      const bodyKey =
        view.kind === 'cancelled'
          ? 'user_management:settings.cancelDeletion.successBody'
          : 'user_management:settings.cancelDeletion.alreadyCancelledBody';
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-success">
            <Check className="h-6 w-6" />
            <h2 className="text-lg font-semibold">{t(titleKey)}</h2>
          </div>
          <p className="text-sm text-muted-foreground">{t(bodyKey)}</p>
          <Button asChild className="w-full">
            <Link to="/login">{t('user_management:settings.cancelDeletion.goToLogin')}</Link>
          </Button>
        </div>
      );
    }
    const titleKey =
      view.kind === 'expired'
        ? 'user_management:settings.cancelDeletion.expiredTitle'
        : 'user_management:settings.cancelDeletion.invalidTitle';
    const bodyKey =
      view.kind === 'expired'
        ? 'user_management:settings.cancelDeletion.expiredBody'
        : 'user_management:settings.cancelDeletion.invalidBody';
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-6 w-6" />
          <h2 className="text-lg font-semibold">{t(titleKey)}</h2>
        </div>
        <p className="text-sm text-muted-foreground">{t(bodyKey)}</p>
        <Button asChild variant="outline" className="w-full">
          <Link to="/login">{t('user_management:settings.cancelDeletion.goToLogin')}</Link>
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 space-y-6">
          <h1 className={PAGE_TITLE_COMPACT}>{t('user_management:settings.cancelDeletion.title')}</h1>
          {renderBody()}
        </CardContent>
      </Card>
    </div>
  );
}
